import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { ApplianceType } from 'src/helper/shared-types';
import {
  addOverrideCategory,
  getAccessCodeHistory,
  getCategoryPreferredVendors,
  getEligibleVendors,
  getMechanicals,
  getPreferredVendors,
  getProperties,
  getPropertyDetails,
  getPropertyMoreDetails,
  getPropertyResidents,
  getPropertyServiceRequests,
  removeOverrideCategory,
  updatePropertyAccessInfo,
} from 'src/redux/properties/api';
import {
  type AccessCodeHistoryEntry,
  type AccessCodeHistoryState,
  type AddOverrideCategoryResponse,
  type ApiRankedVendor,
  type CategoryPreferredVendorsResponse,
  createEligibleVendorsState,
  type EligibleVendorsResponse,
  type InitialStateType,
  type PreferredVendorsListResponse,
  type PropertyDetails,
  type PropertyDetailsAccessInformation,
  type PropertyListItem,
  type PropertyMechanicalsState,
  type PropertyMoreDetails,
  type PropertyMoreDetailsState,
  type PropertyResident,
  type PropertyResidentsState,
  type PropertyServiceRequest,
  type PropertyServiceRequestsState,
  type PVVendor,
} from 'src/redux/properties/types';

/** Ranked API vendors → client vendors in rank order. */
const toRankedVendors = (vendors: ApiRankedVendor[] = []): PVVendor[] =>
  [...vendors]
    .sort((first, second) => first.rank - second.rank)
    .map((vendor) => ({ vendor_id: vendor.vendor_id, name: vendor.name }));

/**
 * GET /properties/{id} sends the access note as `additional_notes`, while the
 * client state (and the updatePropertyAccessInfo reducer below) store it as
 * `access_note` — normalize on ingest so a saved note survives a reload (ORI-487).
 */
const toStateAccessInformation = (
  accessInformation:
    | (Omit<PropertyDetailsAccessInformation, 'access_note'> & {
        access_note?: string | null;
        additional_notes?: string | null;
      })
    | null
    | undefined,
): PropertyDetailsAccessInformation | null => {
  if (!accessInformation) return null;
  const { additional_notes, ...rest } = accessInformation;
  return { ...rest, access_note: rest.access_note ?? additional_notes ?? null };
};

/**
 * Describes the shape that `request()` (src/helper/api.ts) passes to
 * `rejectWithValue` for residents-related thunks.
 *
 * Three variants exist:
 *  • canceled  → { actionType: 'canceled' }
 *  • missingId → { actionType: 'missingId', message: string }
 *  • API error → { ...response.data, status: number, property_uuid: string }
 *
 * `actionType` is a literal union matching the only value produced by the
 * request() cancel branch — keeps parity with src/redux/scheduling/types.ts.
 */
type RejectedPayload = {
  actionType?: 'canceled';
  property_uuid?: string;
  message?: string;
  status?: number;
};

const emptyMechanicalsState = (): PropertyMechanicalsState => ({
  ids: [],
  source: {},
  isLoading: false,
  hasFetched: false,
  error: null,
});

const emptyResidentsState = (): PropertyResidentsState => ({
  ids: [],
  source: {},
  meta: null,
  isLoading: false,
  hasFetched: false,
  error: null,
});

const emptyServiceRequestsState = (): PropertyServiceRequestsState => ({
  ids: [],
  source: {},
  meta: null,
  isLoading: false,
  hasFetched: false,
  error: null,
});

const emptyAccessCodeHistoryState = (): AccessCodeHistoryState => ({
  ids: [],
  source: {},
  meta: null,
  isLoading: false,
  hasFetched: false,
  error: null,
});

const emptyMoreDetailsState = (): PropertyMoreDetailsState => ({
  data: null,
  isLoading: false,
  hasFetched: false,
  error: null,
});

const extraReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  builder.addCase(getProperties().pending, (state: InitialStateType) => {
    state.isFetchingProperties = true;
  });
  builder.addCase(getProperties().fulfilled, (state: InitialStateType, action) => {
    const validItems: PropertyListItem[] = (action?.payload?.data || []).filter(
      (item: PropertyListItem) => Boolean(item?.property_uuid),
    );
    const incomingIds: Array<string> = validItems.map((item) => item.property_uuid);

    const isPaginatedFetch = Boolean(action?.meta?.arg?.cursor);
    // Dedup on append: a property that shifts across a cursor page boundary can
    // arrive on more than one page, and an un-deduped concat produces duplicate
    // rows / duplicate React keys (COM-715). Set preserves first-seen order.
    state.data.data = isPaginatedFetch
      ? [...new Set([...(state?.data?.data || []), ...incomingIds])]
      : incomingIds;

    const newSource = { ...state?.source };
    validItems.forEach((item) => {
      newSource[item.property_uuid] = {
        ...newSource?.[item.property_uuid],
        ...item,
      };
    });
    state.source = newSource;

    state.pagination = {
      nextCursor: action?.payload?.meta?.next ?? null,
    };
    if (action?.payload?.meta?.last_synced_at != null) {
      state.lastSyncedAt = action.payload.meta.last_synced_at;
    }
    if (action?.payload?.meta?.unassigned_properties_count != null) {
      state.unassignedPropertiesCount = action.payload.meta.unassigned_properties_count;
    }
    state.isFetchingProperties = false;
  });
  builder.addCase(getProperties().rejected, (state: InitialStateType) => {
    state.isFetchingProperties = false;
  });

  builder.addCase(getPropertyDetails().pending, (state: InitialStateType) => {
    state.isFetchingPropertyDetails = true;
  });
  builder.addCase(getPropertyDetails().fulfilled, (state: InitialStateType, action) => {
    state.isFetchingPropertyDetails = false;
    const details: PropertyDetails | undefined = action?.payload?.data ?? action?.payload;
    if (details?.property_uuid) {
      const merged = {
        ...state?.detailsSource?.[details.property_uuid],
        ...details,
      };
      if ('access_information' in details) {
        merged.access_information = toStateAccessInformation(details.access_information);
      }
      state.detailsSource = {
        ...state?.detailsSource,
        [details.property_uuid]: merged,
      };
    }
  });
  builder.addCase(getPropertyDetails().rejected, (state: InitialStateType) => {
    state.isFetchingPropertyDetails = false;
  });

  builder.addCase(updatePropertyAccessInfo().pending, (state: InitialStateType) => {
    state.isUpdatingAccessInfo = true;
  });
  builder.addCase(updatePropertyAccessInfo().fulfilled, (state: InitialStateType, action) => {
    state.isUpdatingAccessInfo = false;
    const {
      propertyId,
      accessMethod,
      access_method,
      access_code,
      additional_notes,
      pets,
      access_code_history_count,
    } = action.payload ?? {};
    if (propertyId && state.detailsSource?.[propertyId]) {
      const existing = state.detailsSource[propertyId].access_information;
      // A new history entry is recorded whenever an access code is set or changed.
      // Prefer the count the API returns; otherwise bump it locally so the
      // "View access code history" button surfaces immediately without a refetch.
      const previousHistoryCount = existing?.access_code_history_count ?? 0;
      const accessCodeChanged = access_code != null && access_code !== existing?.access_code;
      state.detailsSource[propertyId].access_information = {
        access_method: accessMethod ??
          existing?.access_method ?? { label: access_method, value: access_method },
        access_code: access_code ?? null,
        access_note: additional_notes ?? null,
        pets: pets ?? existing?.pets ?? { selected: [], other_detail: null },
        access_code_history_count:
          typeof access_code_history_count === 'number'
            ? access_code_history_count
            : previousHistoryCount + (accessCodeChanged ? 1 : 0),
      };
    }
  });
  builder.addCase(updatePropertyAccessInfo().rejected, (state: InitialStateType) => {
    state.isUpdatingAccessInfo = false;
  });

  builder.addCase(getPropertyResidents().pending, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.residentsByProperty?.[propertyUuid] ?? emptyResidentsState();
    // Direct Immer mutation — consistent with the rest of this file.
    state.residentsByProperty[propertyUuid] = { ...current, isLoading: true, error: null };
  });
  builder.addCase(getPropertyResidents().fulfilled, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.payload?.property_uuid;
    if (!propertyUuid) return;
    const residents: PropertyResident[] = Array.isArray(action?.payload?.data)
      ? action.payload.data.filter((item: PropertyResident) => Boolean(item?.resident_id))
      : [];
    const source: { [k: string]: PropertyResident } = {};
    const ids: string[] = [];
    residents.forEach((item) => {
      source[item.resident_id] = item;
      ids.push(item.resident_id);
    });
    // Direct Immer mutation — consistent with the rest of this file.
    state.residentsByProperty[propertyUuid] = {
      ids,
      source,
      meta: action?.payload?.meta ?? null,
      isLoading: false,
      hasFetched: true,
      error: null,
    };
  });
  builder.addCase(getPropertyResidents().rejected, (state: InitialStateType, action) => {
    const rejectedPayload = action?.payload as RejectedPayload | undefined;
    const propertyUuid: string | undefined =
      rejectedPayload?.property_uuid || action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.residentsByProperty?.[propertyUuid] ?? emptyResidentsState();
    const isCanceled = rejectedPayload?.actionType === 'canceled';
    // Direct Immer mutation — consistent with the rest of this file.
    state.residentsByProperty[propertyUuid] = {
      ...current,
      isLoading: false,
      error: isCanceled ? current.error : (rejectedPayload?.message ?? 'Request failed'),
    };
  });

  // ── Property Service Requests (ORI-166) ─────────────────────────────────
  builder.addCase(getPropertyServiceRequests().pending, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.serviceRequestsByProperty?.[propertyUuid] ?? emptyServiceRequestsState();
    state.serviceRequestsByProperty[propertyUuid] = {
      ...current,
      isLoading: true,
      error: null,
    };
  });
  builder.addCase(getPropertyServiceRequests().fulfilled, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.payload?.property_uuid;
    if (!propertyUuid) return;
    const serviceRequests: PropertyServiceRequest[] = Array.isArray(action?.payload?.data)
      ? action.payload.data.filter((item: PropertyServiceRequest) =>
          Boolean(item?.service_request_id),
        )
      : [];
    const meta = action?.payload?.meta ?? null;
    // Pages beyond the first are "load more" fetches — append to the existing
    // list so earlier pages stay visible. Page 1 (initial load and the
    // post-create refetch) replaces, resetting the list to the top.
    const isLoadMore = (meta?.current_page ?? 1) > 1;
    const current = state.serviceRequestsByProperty?.[propertyUuid] ?? emptyServiceRequestsState();
    const source: { [k: string]: PropertyServiceRequest } = isLoadMore ? { ...current.source } : {};
    const ids: string[] = isLoadMore ? [...current.ids] : [];
    serviceRequests.forEach((item) => {
      if (!(item.service_request_id in source)) {
        ids.push(item.service_request_id);
      }
      source[item.service_request_id] = item;
    });
    state.serviceRequestsByProperty[propertyUuid] = {
      ids,
      source,
      meta,
      isLoading: false,
      hasFetched: true,
      error: null,
    };
  });
  builder.addCase(getPropertyServiceRequests().rejected, (state: InitialStateType, action) => {
    const rejectedPayload = action?.payload as RejectedPayload | undefined;
    const propertyUuid: string | undefined =
      rejectedPayload?.property_uuid || action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.serviceRequestsByProperty?.[propertyUuid] ?? emptyServiceRequestsState();
    const isCanceled = rejectedPayload?.actionType === 'canceled';
    state.serviceRequestsByProperty[propertyUuid] = {
      ...current,
      isLoading: false,
      error: isCanceled ? current.error : (rejectedPayload?.message ?? 'Request failed'),
    };
  });

  // ── Property Access Code History ────────────────────────────────────────
  builder.addCase(getAccessCodeHistory().pending, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current =
      state.accessCodeHistoryByProperty?.[propertyUuid] ?? emptyAccessCodeHistoryState();
    state.accessCodeHistoryByProperty[propertyUuid] = {
      ...current,
      isLoading: true,
      error: null,
    };
  });
  builder.addCase(getAccessCodeHistory().fulfilled, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.payload?.property_uuid;
    if (!propertyUuid) return;
    const entries: AccessCodeHistoryEntry[] = Array.isArray(action?.payload?.data)
      ? action.payload.data.filter((item: AccessCodeHistoryEntry) => Boolean(item?.id))
      : [];
    const meta = action?.payload?.meta ?? null;
    // Pages beyond the first are infinite-scroll fetches — append to the
    // existing list so earlier pages stay visible. Page 1 replaces, resetting
    // the list to the top. Same pattern as getPropertyServiceRequests.
    const isLoadMore = (meta?.current_page ?? 1) > 1;
    const current =
      state.accessCodeHistoryByProperty?.[propertyUuid] ?? emptyAccessCodeHistoryState();
    const source: { [k: string]: AccessCodeHistoryEntry } = isLoadMore ? { ...current.source } : {};
    const ids: string[] = isLoadMore ? [...current.ids] : [];
    entries.forEach((item) => {
      if (!(item.id in source)) {
        ids.push(item.id);
      }
      source[item.id] = item;
    });
    state.accessCodeHistoryByProperty[propertyUuid] = {
      ids,
      source,
      meta,
      isLoading: false,
      hasFetched: true,
      error: null,
    };
  });
  builder.addCase(getAccessCodeHistory().rejected, (state: InitialStateType, action) => {
    const rejectedPayload = action?.payload as RejectedPayload | undefined;
    const propertyUuid: string | undefined =
      rejectedPayload?.property_uuid || action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current =
      state.accessCodeHistoryByProperty?.[propertyUuid] ?? emptyAccessCodeHistoryState();
    const isCanceled = rejectedPayload?.actionType === 'canceled';
    state.accessCodeHistoryByProperty[propertyUuid] = {
      ...current,
      isLoading: false,
      error: isCanceled ? current.error : (rejectedPayload?.message ?? 'Request failed'),
    };
  });

  // ── Property More Details (ORI-174) ─────────────────────────────────────
  builder.addCase(getPropertyMoreDetails().pending, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.moreDetailsByProperty?.[propertyUuid] ?? emptyMoreDetailsState();
    state.moreDetailsByProperty[propertyUuid] = { ...current, isLoading: true, error: null };
  });
  builder.addCase(getPropertyMoreDetails().fulfilled, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.payload?.property_uuid;
    if (!propertyUuid) return;
    // `action.payload` is guaranteed non-null at this point because we just
    // read a string property off it.
    const moreDetails = action.payload as PropertyMoreDetails;
    state.moreDetailsByProperty[propertyUuid] = {
      data: moreDetails,
      isLoading: false,
      hasFetched: true,
      error: null,
    };
  });
  builder.addCase(getPropertyMoreDetails().rejected, (state: InitialStateType, action) => {
    const rejectedPayload = action?.payload as RejectedPayload | undefined;
    const propertyUuid: string | undefined =
      rejectedPayload?.property_uuid || action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.moreDetailsByProperty?.[propertyUuid] ?? emptyMoreDetailsState();
    const isCanceled = rejectedPayload?.actionType === 'canceled';
    state.moreDetailsByProperty[propertyUuid] = {
      ...current,
      isLoading: false,
      error: isCanceled ? current.error : (rejectedPayload?.message ?? 'Request failed'),
    };
  });

  // ── Property Mechanicals (ORI-168) ──────────────────────────────────────
  builder.addCase(getMechanicals().pending, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.mechanicalsByProperty?.[propertyUuid] ?? emptyMechanicalsState();
    state.mechanicalsByProperty[propertyUuid] = { ...current, isLoading: true, error: null };
  });
  builder.addCase(getMechanicals().fulfilled, (state: InitialStateType, action) => {
    const propertyUuid: string | undefined = action?.payload?.property_uuid;
    if (!propertyUuid) return;
    const appliances: ApplianceType[] = Array.isArray(action?.payload?.data)
      ? action.payload.data.filter((item: ApplianceType) => Boolean(item?.appliance_id))
      : [];
    const source: { [applianceId: string]: ApplianceType } = {};
    const ids: string[] = [];
    appliances.forEach((item) => {
      source[item.appliance_id] = item;
      ids.push(item.appliance_id);
    });
    state.mechanicalsByProperty[propertyUuid] = {
      ids,
      source,
      isLoading: false,
      hasFetched: true,
      error: null,
    };
  });
  builder.addCase(getMechanicals().rejected, (state: InitialStateType, action) => {
    const rejectedPayload = action?.payload as RejectedPayload | undefined;
    const propertyUuid: string | undefined =
      rejectedPayload?.property_uuid || action?.meta?.arg?.property_uuid;
    if (!propertyUuid) return;
    const current = state.mechanicalsByProperty?.[propertyUuid] ?? emptyMechanicalsState();
    const isCanceled = rejectedPayload?.actionType === 'canceled';
    state.mechanicalsByProperty[propertyUuid] = {
      ...current,
      isLoading: false,
      error: isCanceled ? current.error : (rejectedPayload?.message ?? 'Request failed'),
    };
  });

  // ── Preferred Vendors: list ──────────────────────────────────────────────
  builder.addCase(getPreferredVendors().pending, (state) => {
    state.preferredVendors.isLoadingList = true;
  });

  builder.addCase(getPreferredVendors().fulfilled, (state, action) => {
    const payload = action.payload as PreferredVendorsListResponse;
    const preferredVendors = state.preferredVendors;
    const previousSelectedCategoryId = preferredVendors.selectedCategoryId;

    const overrideValues = new Set<string>();

    preferredVendors.overriddenCategories = (payload.override_categories ?? []).map((item) => {
      overrideValues.add(item.value);
      return {
        id: item.value,
        name: item.label,
        previewNames: item.preferred_vendors ?? [],
      };
    });

    preferredVendors.categories = (payload.categories ?? []).map((category) => ({
      value: category.value,
      label: category.label,
    }));

    preferredVendors.categorySearchState = payload.category_search_state ?? null;

    // Drop a stale selection (and its now-orphaned detail) if it's no longer an override.
    if (
      preferredVendors.selectedCategoryId &&
      !overrideValues.has(preferredVendors.selectedCategoryId)
    ) {
      preferredVendors.selectedCategoryId = null;
      preferredVendors.detail = null;
    }

    // Auto-select the first category if nothing is selected (initial load or
    // after a stale selection was just dropped above).
    if (!preferredVendors.selectedCategoryId && preferredVendors.overriddenCategories.length > 0) {
      preferredVendors.selectedCategoryId = preferredVendors.overriddenCategories[0].id;
    }

    // Selection changed — clear the previous category's stale eligible vendors.
    if (preferredVendors.selectedCategoryId !== previousSelectedCategoryId) {
      preferredVendors.eligibleVendors = createEligibleVendorsState();
    }

    // Reflects the last settled fetch so the tab can distinguish a filtered-empty
    // list ("no results") from a genuinely empty one ("no overrides").
    preferredVendors.isListFiltered = Boolean(action.meta.arg?.search);
    preferredVendors.isLoadingList = false;
    preferredVendors.hasFetched = true;
  });

  builder.addCase(getPreferredVendors().rejected, (state) => {
    // Failure is surfaced via toast in the actions hook (house pattern); state
    // just clears the loading flag.
    const preferredVendors = state.preferredVendors;
    preferredVendors.isLoadingList = false;
    preferredVendors.hasFetched = true;
  });

  // ── Preferred Vendors: per-category detail ────────────────────────────────
  builder.addCase(getCategoryPreferredVendors().pending, (state) => {
    state.preferredVendors.isLoadingDetail = true;
    state.preferredVendors.detailFailed = false;
  });

  builder.addCase(getCategoryPreferredVendors().fulfilled, (state, action) => {
    const payload = action.payload as CategoryPreferredVendorsResponse;
    const categoryId = payload.category?.value ?? payload.category_id;
    const preferredVendors = state.preferredVendors;
    preferredVendors.isLoadingDetail = false;
    if (!categoryId) return;
    // Ignore a response that arrives after the user moved to another category.
    if (preferredVendors.selectedCategoryId && preferredVendors.selectedCategoryId !== categoryId)
      return;
    preferredVendors.detailFailed = false;

    const overriddenName = preferredVendors.overriddenCategories.find(
      (item) => item.id === categoryId,
    )?.name;
    preferredVendors.detail = {
      category_id: categoryId,
      name: payload.category?.label ?? overriddenName ?? '',
      marketDefaultLabel: payload.default_vendors.map((vendor) => vendor.name).join(', '),
      defaultVendors: toRankedVendors(payload.default_vendors),
      subCategories: (payload.sub_categories ?? []).map((sub) => ({
        subcategory_id: sub.value,
        name: sub.label,
        is_override: !sub.is_inherit,
        vendors: toRankedVendors(sub.vendors),
      })),
    };
  });

  builder.addCase(getCategoryPreferredVendors().rejected, (state, action) => {
    const preferredVendors = state.preferredVendors;
    preferredVendors.isLoadingDetail = false;
    // Only flag failure if it's still the open category (ignore stale rejections).
    const requestedCategoryId = action.meta?.arg?.category_id as string | undefined;
    if (!requestedCategoryId || requestedCategoryId === preferredVendors.selectedCategoryId) {
      preferredVendors.detailFailed = true;
    }
  });

  // ── Preferred Vendors: per-category eligible vendors ───────────────────────
  // Fetched once per category; pickers and inheriting subs filter it client-side.
  // `hasFetched` is the loading signal.
  builder.addCase(getEligibleVendors().fulfilled, (state, action) => {
    const preferredVendors = state.preferredVendors;
    // Ignore a response that arrives after the user moved to another category.
    const requestedCategoryId = (action.meta?.arg as { category_id?: string })?.category_id;
    if (
      requestedCategoryId &&
      preferredVendors.selectedCategoryId &&
      requestedCategoryId !== preferredVendors.selectedCategoryId
    ) {
      return;
    }
    const rows = (action.payload as EligibleVendorsResponse) ?? [];
    const eligible = preferredVendors.eligibleVendors;
    eligible.vendorsById = {};
    eligible.allVendorIds = [];

    for (const row of rows) {
      eligible.vendorsById[row.vendor_id] = {
        vendor_id: row.vendor_id,
        name: row.name,
        is_market_preferred: Boolean(row.is_market_preferred),
        has_all_skills: Boolean(row.has_all_skills),
        skills: row.skills ?? [],
      };
      eligible.allVendorIds.push(row.vendor_id);
    }
    eligible.hasFetched = true;
  });

  builder.addCase(getEligibleVendors().rejected, (state, action) => {
    // A canceled request (superseded by a category switch) isn't a failure.
    if ((action.payload as { actionType?: string } | undefined)?.actionType === 'canceled') return;
    // Failure toasts in the hook; the empty map falls back to unfiltered defaults.
    const eligible = state.preferredVendors.eligibleVendors;
    eligible.hasFetched = true;
  });

  // ── Preferred Vendors: add override category ──────────────────────────────
  builder.addCase(addOverrideCategory().fulfilled, (state, action) => {
    const payload = action.payload as AddOverrideCategoryResponse;
    if (!payload.category?.value) return;
    const preferredVendors = state.preferredVendors;

    const categoryId = payload.category.value;
    const categoryName = payload.category.label;
    const defaultVendors = toRankedVendors(payload.default_vendors);

    // Open the new category straight from the add response (no detail fetch). Drop
    // the old eligible vendors — the category effect refetches them for the new one.
    preferredVendors.eligibleVendors = createEligibleVendorsState();
    preferredVendors.detail = {
      category_id: categoryId,
      name: categoryName,
      marketDefaultLabel: payload.default_vendors.map((vendor) => vendor.name).join(', '),
      defaultVendors,
      subCategories: (payload.sub_categories ?? []).map((sub) => ({
        subcategory_id: sub.value,
        name: sub.label,
        is_override: !sub.is_inherit,
        vendors: toRankedVendors(sub.vendors),
      })),
    };
    preferredVendors.selectedCategoryId = categoryId;
    preferredVendors.detailFailed = false;

    if (!preferredVendors.overriddenCategories.some((item) => item.id === categoryId)) {
      preferredVendors.overriddenCategories.push({
        id: categoryId,
        name: categoryName,
        previewNames: payload.default_vendors.map((vendor) => vendor.name),
      });
    }
  });

  // ── Preferred Vendors: remove override category ───────────────────────────
  builder.addCase(removeOverrideCategory().fulfilled, (state, action) => {
    const categoryId = (action.meta?.arg as { category_id?: string })?.category_id;
    if (!categoryId) return;
    const preferredVendors = state.preferredVendors;

    preferredVendors.overriddenCategories = preferredVendors.overriddenCategories.filter(
      (item) => item.id !== categoryId,
    );

    if (preferredVendors.selectedCategoryId === categoryId) {
      preferredVendors.selectedCategoryId = preferredVendors.overriddenCategories[0]?.id ?? null;
      // Clear the open detail; the select effect refetches the next category
      // (detail + eligible vendors).
      preferredVendors.detail = null;
      preferredVendors.eligibleVendors = createEligibleVendorsState();
    }
  });
};

export default extraReducers;
